import { Injectable, Injector } from '@angular/core';
import { Http } from '@angular/http';
import { Observable } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { AppConfigurationService } from '../common/app-configuration.service';
import { CRUDService } from '../common/crud.service';
import { DataQueryService } from '../common/data-query.service';
import { EventsManagerService } from '../common/events-manager.service';
import { HttpInterceptor } from '../common/http-interceptor.service';
import { IRequestOptions } from '../common/request-options.interface';
import { DatabaseService } from '../storage/database.service';
import { SyncService } from '../storage/sync.service';
import { MetricModel } from '../models/metric.model';

@Injectable()
export class MetricsService extends CRUDService {
  protected backendDataLimit: number = 250;

  constructor(
    http: HttpInterceptor,
    authService: AuthService,
    public eventsService: EventsManagerService,
    public AppConfiguration: AppConfigurationService,
    injector: Injector
  ) {
    super(
      'metrics',
      http,
      authService,
      eventsService,
      AppConfiguration,
      injector
    );
  }

  public createModel(data: any): MetricModel {
    return new MetricModel(this, data);
  }

  public listBase(query?: any, options?: IRequestOptions): Observable<any> {
    let path: any = null;
    let data: any = {};
    if (query && query.type) {
      path = 'enhanced';
      data.type = query.type;
      delete query.type;
    }
    data = Object.assign({}, data, this.buildListData(query, options));

    return this.get(path, data, options).map((apiResponse) => {
      return this.processResponse(apiResponse);
    });
  }

  public retrieveMetricsCustom(
    metricQuery?: any,
    options?: IRequestOptions
  ): Observable<MetricModel[]> {
    return super.retrieveAll(1, metricQuery, options);
  }

  public retrieveMetrics(
    metricQuery?: {
      name: string;
      resource?: string;
      resourceId?: string;
      minDate?: any;
      maxDate?: any;
    },
    options?: IRequestOptions,
    useCreatedAt: boolean = true
  ): Observable<MetricModel[]> {
    return super.retrieveAll(
      1,
      this.buildMetricsQuery(metricQuery, useCreatedAt),
      options
    );
  }

  public retrieveMetricsEnhanced(
    type: string,
    metricQuery?: {
      name: string;
      resource?: string;
      resourceId?: string;
      minDate?: any;
      maxDate?: any;
      value?: any;
    },
    options?: IRequestOptions,
    useCreatedAt: boolean = true
  ): Observable<MetricModel[]> {
    const query = this.buildMetricsQuery(metricQuery, useCreatedAt);
    query.type = type;

    return super.retrieveAll(1, query, options);
  }

  private buildMetricsQuery(
    metricQuery?: {
      name: string;
      resource?: string;
      resourceId?: string;
      minDate?: any;
      maxDate?: any;
      customData?: any;
    },
    useCreatedAt: boolean = true
  ): any {
    let query: any;

    if (metricQuery) {
      const { name, resource, resourceId, minDate, maxDate, customData } = metricQuery;
      query = { name };

      if (customData) {
        Object.keys(customData).forEach((c) => {
          query[c] = customData[c];
        });
      }

      if (resource) {
        query.resource = resource;
      }

      if (resourceId) {
        query.resourceId = resourceId;
      }

      if (minDate && maxDate) {
        if (useCreatedAt) {
          query.createdAt = { $gte: minDate, $lte: maxDate };
        } else {
          query['value.timestamp'] = { $gte: minDate, $lte: maxDate };
        }
      } else if (minDate) {
        if (useCreatedAt) {
          query.createdAt = { $gte: minDate };
        } else {
          query['value.timestamp'] = { $gte: minDate };
        }
      } else if (maxDate) {
        if (useCreatedAt) {
          query.createdAt = { $lte: maxDate };
        } else {
          query['value.timestamp'] = { $lte: maxDate };
        }
      }
    }

    return query;
  }

  private toEpoch(date: string) {
    return new Date(date).getTime() / 1000;
  }
}
